// GovernanceDiscovery — discovery/drift dashboard (PAM Epic 11, Task 8).
//
// OIDCPAM Gate-6 Phase 6b: migrated off MUI onto
// @devnw/cyber-iam/rbac/AccessDiscoveryTable — the read-only discovered-access
// inventory + drift/hygiene flag badges + Run-Discovery/Refresh parity
// component. AccessDiscoveryTable's onRunDiscovery/onRefresh callbacks are
// SYNC notify (no built-in inFlight guard) — this page still owns the
// `running` flag, the `runNotice` success-banner text, both API calls, and
// the wire-mapping (../api/governanceDiscoveryMapping).
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import AccessDiscoveryTable from '@devnw/cyber-iam/rbac/AccessDiscoveryTable/AccessDiscoveryTable'
import type {
  DiscoveredAccessRecord,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import {
  toDiscoveredAccessRecord,
  type WireDiscoveredAccess,
} from '../api/governanceDiscoveryMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

export const GovernanceDiscovery = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [records, setRecords] = useState<DiscoveredAccessRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [runNotice, setRunNotice] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (!tenant) return
    setLoading(true)
    setError(null)
    try {
      const data = await client.adminGovernanceListDiscovery({ tenantId: tenant })
      // The generated client's fromWire() camelCases the opaque
      // Array<Record<string, unknown>> response — see
      // ../api/governanceDiscoveryMapping.ts for the real (camelCase) shape.
      setRecords(
        ((data.items ?? []) as unknown as WireDiscoveredAccess[]).map(
          toDiscoveredAccessRecord
        )
      )
    } catch (e: unknown) {
      setError(errMessage(e))
    } finally {
      setLoading(false)
    }
  }, [tenant, client])

  useEffect(() => {
    load().then(() => {}).catch(() => {})
  }, [load])

  // AccessDiscoveryTable's onRunDiscovery is a SYNC notify callback (no
  // Promise return, no built-in inFlight guard) — this page owns `running`
  // and disables the Run Discovery button itself via the prop.
  const handleRunDiscovery = (): void => {
    if (!tenant || running) return
    setRunning(true)
    setRunNotice(null)
    setError(null)
    client
      .adminGovernanceRunDiscovery({ tenantId: tenant })
      .then(() => {
        setRunNotice('Discovery run started. Results will update shortly.')
        return load()
      })
      .catch((e: unknown) => setError(errMessage(e)))
      .finally(() => setRunning(false))
  }

  // AccessDiscoveryTable's onRefresh receives the current subject/resource
  // filter text, but the backend has no server-side filter param for this
  // endpoint (AdminListDiscoveryReq only takes tenantId) — the component
  // already applies the filter client-side, so refresh always just refetches.
  const handleRefresh = (): void => {
    load().then(() => {}).catch(() => {})
  }

  if (!tenant) {
    return (
      <PageContainer>
        <MutedText>Select a tenant to view the discovery dashboard.</MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <AccessDiscoveryTable
        testID="governance-discovery"
        title="Access discovery & drift"
        description="Inventories all discovered access entitlements and flags orphaned, stale, over-privileged, or drifted grants."
        records={records}
        running={running}
        runNotice={runNotice ?? undefined}
        loading={loading}
        error={error ?? undefined}
        onRunDiscovery={handleRunDiscovery}
        onRefresh={handleRefresh}
        emptyText="No discovered access records — run discovery to populate."
      />
    </PageContainer>
  )
}
